import { singleton } from "tsyringe";
import { html, HTMLTemplateResult, nothing } from "lit";
import { when } from "lit/directives/when.js";
import { keyed } from "lit/directives/keyed.js";
import { classMap } from "lit/directives/class-map.js";
import { translate, get } from "lit-translate";
import { DirectiveResult } from "lit/directive.js";
import { BindToDirective } from "@ha/formhandler";
import { IssueStatus } from "../domain/entities/issue-status.entity";
import { IssueType } from "../domain/entities/issue-type.entity";
import { Project } from "../domain/entities/project.entity";
import { Keyword } from "../domain/entities/keyword.entity";
import { Employee } from "../domain/entities/employee.entity";
import { Issue } from "../domain/entities/issue.entity";
import { ISSUE_TYPE_PUBLIC_HOLIDAY, ISSUE_TYPE_VACATION } from "../constants";
import { KeyboardActivationHelper } from "../helpers/keyboard-activation.helper";
import { typeFamilyItemFilter } from "../components/_dialogs/ha-dialog-issue-edit/issue-type-picker.helper";
import { belongsToRoot } from "../domain/helpers/issue-type-root.helper";
import '@o2d/textarea';

/**
 * Service for rendering Issue dialog UI templates.
 * 
 * This service implements the Template Pattern by separating UI rendering
 * from business logic. All templates are pure functions that receive their
 * state and callbacks as parameters.
 * 
 * **Template Pattern Benefits:**
 * - Pure templates without business logic
 * - Testable with mock parameters
 * - Reusable across different dialogs
 * - Clear separation of concerns
 * 
 * **Parameter Injection Pattern:**
 * - State: Current UI state (booleans, values)
 * - Callbacks: Event handlers
 * - Data: External data needed for rendering
 */
@singleton()
export class IssueDialogRendererService {
    
    /**
     * Renders the top button bar with action buttons and status selector.
     * 
     * **Extracted from:** Dialog's _renderTopBar method (45 lines)
     * 
     * **UI Components:**
     * - Save button (with savable state indication)
     * - Reset button (enabled when form is dirty)
     * - Delete button (enabled based on permissions)
     * - Clone button (always enabled)
     * - Copy Link button (with HTTPS detection)
     * - Status combobox (hidden for absence types)
     * 
     * @param bindTo Form binding directive function
     * @param state UI state object with boolean flags
     * @param callbacks Event handler functions
     * @param quickLink The quick link URL for the issue
     * @returns Lit HTML template
     */
    renderTopBar(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        state: {
            isSavable: boolean,
            isDirty: boolean,
            isDeletable: boolean,
            isTypeOfAbsence: boolean,
            isStatusless: boolean,
            isSaveAndCloseEnabled: boolean
        },
        callbacks: {
            onSave: () => void,
            onReset: () => void,
            onDelete: () => void,
            onClone: () => void,
            onCopyLink: () => void,
            onSelectLink: (evt: Event) => void,
            onSaveAndClose: () => void
        },
        quickLink: string
    ): HTMLTemplateResult {
        return html`
            <div class="issue-button-group button-group">
                <button type="button"
                        class="save buttonform-marker ${classMap({savable: state.isSavable})}"
                        ?disabled=${!state.isSavable}
                        @click=${callbacks.onSave}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker ${classMap({savable: state.isSaveAndCloseEnabled})}"
                        ?disabled=${!state.isSaveAndCloseEnabled}
                        @click=${callbacks.onSaveAndClose}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker"
                        ?disabled=${!state.isDirty}
                        @click=${callbacks.onReset}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="delete buttonform-marker"
                        ?disabled=${!state.isDeletable}
                        @click=${callbacks.onDelete}
                
                        data-label=${translate('general.delete')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="clone buttonform-marker"
                        @click=${callbacks.onClone}
                
                        data-label=${translate('general.clone')}><div class="hover-effect"></div>
                </button>

                ${when(window.isSecureContext, () => html`
                    <button type="button"
                            class="quicklink buttonform-marker"
                            @click=${callbacks.onCopyLink}
                    
                            data-label=${translate('general.copy-link')}><div class="hover-effect"></div>
                    </button>
                `, () => html`
                    <button type="button"
                            class="quicklink without-https buttonform-marker"
                            title="Quicklink: Zum markieren, anklicken"
                            @click=${callbacks.onSelectLink}
                    >
                        <span class="quicklink-text">${quickLink}</span>
                        <div class="hover-effect"></div>
                    </button>
                `)}
            </div>
            ${when(!state.isStatusless, () => html`
                <ha-combobox class="issue-status" data-type="status" theme="visible"
                             ${bindTo('status')}
                             .entity=${IssueStatus}
                             ?readonly=${false}
                ></ha-combobox>
            `)}
        `;
    }

    /**
     * Renders the title section (`.wrapper-title-quicklink-daterange`). The
     * date range moved into `.wrapper-type-board-labels` (Anke, 2026-10-01),
     * see {@link renderDateRange}.
     * 
     * @param bindTo Form binding directive function
     * @param isReadOnly Whether fields should be read-only
     * @param issueType Current issue type — the Absence family shows "Urlaub" as the
     *   title placeholder (Anke, 2026-08-19) instead of the generic one, so
     *   a freshly opened "Neuen Urlaub"-dialog hints at the auto-fill even
     *   before an employee is picked (see `vacation-auto-title.helper.ts`,
     *   which then overwrites this with the actual value once one is).
     * @returns Lit HTML template
     */
    renderTitle(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        isReadOnly: boolean,
        issueType: IssueType | null
    ): HTMLTemplateResult {
        const titlePlaceholder = belongsToRoot(issueType, ISSUE_TYPE_VACATION)
            ? translate('ha-boards-view.edit-issue.title-placeholder-absence')
            : translate('components.ha-dialog.title');

        return html`
            <div class="wrapper-title-quicklink-daterange">
                <div class="issue-title-quicklink">
                    <o2d-textarea
                            class="issue-title max9999-flexrow max1600-flexcolumn max992-flexrow  max355-flexcolumn"
                            label=${translate('components.ha-dialog.title')}
                            data-type="title"
                            placeholder=${titlePlaceholder}
                            ${bindTo('title')}
                            ?readonly=${isReadOnly}
                    ></o2d-textarea>
                </div>
            </div>
        `;
    }

    /**
     * The date range field. Rendered as the first field of
     * `.wrapper-type-board-labels` (Anke, 2026-10-01).
     *
     * @param bindTo Form binding directive function
     * @param isReadOnly Whether the field should be read-only
     * @returns Lit HTML template
     */
    renderDateRange(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        isReadOnly: boolean
    ): HTMLTemplateResult {
        return html`
            <ha-input-date-range class="issue-date-range" data-type="timeslot"
                                 label=${translate('ha-boards-view.edit-issue.timeslot')}
                                 ${bindTo('timeRange')}
                                 ?readonly=${isReadOnly}
            ></ha-input-date-range>
        `;
    }

    /**
     * Renders the issue type, project board and labels fields. They sit inside
     * `.wrapper-type-board-labels`, which `renderCompleteForm` builds around
     * them together with the date range, the assignments and the hidden infos.
     *
     * **Extracted from:** Dialog's _renderTypeBoardAndLabels method (32 lines)
     *
     * **UI Components:**
     * - Issue type combobox, only where the issue's family offers a choice (D7), listing only that
     *   family (D56). Keyed by the root: the combobox loads its list once, so a dialog tab of another
     *   family must get its own element rather than reuse this one's list.
     * - Project board combobox (hidden for absence types)
     * - Keywords/labels multi-select (hidden for absence types)
     *
     * **Business Rules Handled:**
     * - Absence types don't have assignees or keywords
     *
     * @param bindTo Form binding directive function
     * @param isReadOnly Whether fields should be read-only
     * @param isTypeOfAbsence Whether current type is absence
     * @param typePicker Whether the type row is shown, and the root whose family it lists
     * @returns Lit HTML template
     */
    renderTypeBoardAndLabels(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        isReadOnly: boolean,
        isTypeOfAbsence: boolean,
        typePicker: { visible: boolean, rootUid: string | null, keepAbstractRoot: boolean }
    ): HTMLTemplateResult {
        return html`
                ${when(typePicker.visible && typePicker.rootUid, () => keyed(typePicker.rootUid, html`
                    <ha-combobox class="issue-type  max9999-flexrow max355-flexcolumn" data-type="type"
                                 label=${translate('general.type')}
                                 .itemFilter=${typeFamilyItemFilter(typePicker.rootUid!, typePicker.keepAbstractRoot)}
                                 ${bindTo('type')}
                                 .entity=${IssueType}
                                 with-filter
                                 ?readonly=${isReadOnly}
                    ></ha-combobox>
                `))}
                ${when(!isTypeOfAbsence, () => html`
                    <ha-combobox class="issue-board  max9999-flexrow max355-flexcolumn" data-type="board"
                                 label=${translate('general.board')}
                                 ${bindTo('project')}
                                 .entity=${Project}
                                 with-filter
                                 with-clear-button
                                 ?readonly=${isReadOnly}
                    ></ha-combobox>
                `)}
                ${when(!isTypeOfAbsence, () => html`
                    <ha-combobox-multi class="issue-label max9999-flexrow max355-flexcolumn" data-type="label"
                                       label=${translate('general.label')}
                                       theme="visible"
                                       ${bindTo('keywords')}
                                       .entity=${Keyword}
                                       with-filter
                                       ?readonly=${isReadOnly}
                    ></ha-combobox-multi>
                `)}
        `;
    }

    /**
     * Renders the employee assignments section.
     * 
     * **Extracted from:** Dialog's _renderEmployeeAssignments method (23 lines)
     * 
     * **UI Components:**
     * - Employee assignment component with max limit
     * 
     * **Business Rules Handled:**
     * - PUBLIC_HOLIDAY: No assignments (returns nothing)
     * - VACATION: Max 1 assignment
     * - Others: Unlimited assignments
     * 
     * Bugreport 2026-08-19 (Anke) — "beim Urlaub erstellen ist nur das
     * Pluszeichen zu sehen, nicht die Auswahlbox selbst": `.issue`/`.max`
     * MUST be bound before the `${bindTo(...)}` ElementPart in this template
     * (fixed below) — lit-html applies bindings on one element in template
     * order, and `BindToDirective.update()` synchronously sets `.value` on
     * `<ha-employee-issue-assignment>`, which for an empty initial array
     * auto-adds one row via `_onClickAdd()` — but THAT guards on `this.issue`
     * already being set (`if (!issue ...) return`). With `bindTo` written
     * before `.issue=` (the previous order), `.issue` was still `null` at
     * that exact synchronous moment, so the auto-add silently no-opped,
     * leaving only the "+" button. Reordering (`.issue`/`.max` first) makes
     * both already set by the time `bindTo` fires.
     *
     * @param bindTo Form binding directive function
     * @param issueType Current issue type
     * @param selectedEntity Current issue entity
     * @param isReadOnly Whether field should be read-only
     * @returns Lit HTML template or nothing
     */
    renderEmployeeAssignments(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        issueType: IssueType | null,
        selectedEntity: Issue,
        isReadOnly: boolean,
        onUserInput: () => void
    ): HTMLTemplateResult | typeof nothing {
        // Business rule: Public holidays (root and children) have no assignments
        if (belongsToRoot(issueType, ISSUE_TYPE_PUBLIC_HOLIDAY)) {
            return nothing;
        }

        // Business rule: an absence (root and children) has max 1 assignment, others unlimited
        const maxAssignments = belongsToRoot(issueType, ISSUE_TYPE_VACATION) ? 1 : Infinity;

        return html`
            <ha-employee-issue-assignment
                    class="issue-assignment max9999-flexrow max1200-flexcolumn max992-flexrow max355-flexcolumn"
                    data-type="assignee"
                    label=${translate('general.assignee')}
                    ?readonly=${isReadOnly}
                    .issue=${selectedEntity}
                    .max=${maxAssignments}
                    ${bindTo('employeeToIssueAssignments')}
                    @value-changed=${onUserInput}
            ></ha-employee-issue-assignment>
        `;
    }

    /**
     * The bar above the creator + audit-log region. A keyboard-reachable
     * toggle button — except for vacation/public-holiday issues: Anke's CSS
     * hides both fields there, so the bar stays visible (her decision) but
     * plain, with no tab stop and no "expanded/collapsed" announcement for an
     * empty region (review run 04, 2026-09-25).
     *
     * @param state Absence flag, collapsed state and the id of the region the bar controls
     * @param onToggleHiddenInfos Toggles the region (click and Enter/Space)
     * @returns The bar's HTML template
     */
    renderHiddenInfosBar(
        state: { isTypeOfAbsence: boolean, isHiddenInfosCollapsed: boolean, hiddenInfosRegionId: string },
        onToggleHiddenInfos: () => void
    ): HTMLTemplateResult {
        if (state.isTypeOfAbsence) {
            return html`<div class="dialog-hidden-infos--button"></div>`;
        }
        return html`
            <div class="dialog-hidden-infos--button"
                 role="button"
                 tabindex="0"
                 aria-expanded=${!state.isHiddenInfosCollapsed}
                 aria-controls=${state.hiddenInfosRegionId}
                 title=${translate('components.ha-dialog.hidden-infos-toggle')}
                 @click=${onToggleHiddenInfos}
                 @keydown=${KeyboardActivationHelper.onEnterOrSpace(onToggleHiddenInfos)}
            ></div>`;
    }

    /**
     * Renders the main content wrapper with all sections.
     * 
     * This is a convenience method that combines all render methods
     * into a complete form layout. It can be used when you want the
     * standard layout, or you can call individual methods for custom layouts.
     * 
     * @param bindTo Form binding directive function
     * @param state Complete state object for all sections
     * @param callbacks Event handler functions
     * @param data Additional data needed for rendering
     * @returns Complete form HTML template
     */
    renderCompleteForm(
        bindTo: (fieldPath: string) => DirectiveResult<typeof BindToDirective>,
        state: {
            isSavable: boolean,
            isDirty: boolean,
            isDeletable: boolean,
            isTypeOfAbsence: boolean,
            isStatusless: boolean,
            isHiddenInfosCollapsed: boolean,
            hiddenInfosRegionId: string,
            isReadOnly: boolean,
            isSaveAndCloseEnabled: boolean,
            typePicker: { visible: boolean, rootUid: string | null, keepAbstractRoot: boolean }
        },
        callbacks: {
            onSave: () => void,
            onReset: () => void,
            onDelete: () => void,
            onClone: () => void,
            onCopyLink: () => void,
            onSelectLink: (evt: Event) => void,
            onSaveAndClose: () => void,
            onUserInput: () => void,
            onToggleHiddenInfos: () => void
        },
        data: {
            quickLink: string,
            issueType: IssueType | null,
            selectedEntity: Issue
        }
    ): HTMLTemplateResult {
        // `.dialog-hidden-infos` (Anke's flex container) stays as it is; the fold
        // lives in its own wrapper `.dialog-hidden-infos__collapse` (one grid row,
        // 1fr ↔ 0fr, dialog-issue-edit.css), so her rules on the container and on
        // `.issue-creator` keep applying unchanged. A TS comment on purpose: an
        // HTML comment in the template would be shipped into the DOM.
        return html`
            ${this.renderTopBar(bindTo, state, callbacks, data.quickLink)}
            ${this.renderTitle(bindTo, state.isReadOnly, data.issueType)}
            <div class="wrapper-type-board-labels">
                ${this.renderDateRange(bindTo, state.isReadOnly)}
                ${this.renderEmployeeAssignments(bindTo, data.issueType, data.selectedEntity, state.isReadOnly, callbacks.onUserInput)}
                ${this.renderTypeBoardAndLabels(bindTo, state.isReadOnly, state.isTypeOfAbsence, state.typePicker)}
                <div class="dialog-hidden-infos">
                    ${this.renderHiddenInfosBar(state, callbacks.onToggleHiddenInfos)}
                    <div id=${state.hiddenInfosRegionId}
                         class=${classMap({'dialog-hidden-infos__collapse': true, 'is-collapsed': state.isHiddenInfosCollapsed})}>
                        <div class="dialog-hidden-infos__content">
                            <ha-combobox class="issue-creator  max9999-flexrow max355-flexcolumn" data-type="creator"
                                         label=${translate('general.creator')}
                                         ${bindTo('creator')}
                                         .entity=${Employee}
                                         with-filter
                                         ?readonly=${state.isReadOnly}
                            ></ha-combobox>
                            <ha-audit-log-viewer class="max9999-flexcolumn"
                                                 .entities=${[data.selectedEntity]}
                            ></ha-audit-log-viewer>
                        </div>
                    </div>
                </div>
            </div>
            ${when(!state.isTypeOfAbsence, () => html`
                <ha-rte class="issue-description" data-type="description"
                        label=${translate('components.ha-dialog.description')}
                        ${bindTo('description')}
                        ?readonly=${state.isReadOnly}
                ></ha-rte>
            `)}
        `;
    }
}